/**
 * The account screen: profile, change password, the games this account played and hosted, its
 * questions in the bank, and read-only lists of the templates and clue packs it owns. Everything
 * here is read from the server, so it is the same on any device (My Games is per device).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { bank, type PoolQuestion } from '@games/jeopardy/ui/content';
import { api, type FormatSummary, type PackSummary, type SessionSummary } from '../lib/api';
import { authApi, MIN_PASSWORD_LENGTH, type AccountSummary } from '../lib/auth';
import { noteSignedIn } from '../lib/whoami';
import { Toast } from '../kit/Toast';
import { AuthGate } from '../components/AuthGate';
import {
  ACCOUNT_SHOWN,
  deleteTemplate,
  HostedList,
  PacksPanel,
  QuestionsPanel,
  TemplatesPanel,
} from '../components/AccountPanels';
import { ResultsList } from '../components/ResultsList';

export function Admin() {
  return (
    <AuthGate title="Account">
      <AccountInner />
    </AuthGate>
  );
}

function AccountInner() {
  const [account, setAccount] = useState<AccountSummary | null>(null);
  const [formats, setFormats] = useState<FormatSummary[]>([]);
  const [packs, setPacks] = useState<PackSummary[]>([]);
  const [gameNames, setGameNames] = useState<Map<string, string>>(new Map());
  const [hosted, setHosted] = useState<SessionSummary[]>([]);
  const [questions, setQuestions] = useState<PoolQuestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [me, mine, myPacks, games, sessions, mineQuestions] = await Promise.all([
        authApi.me(),
        api.listFormats(true),
        api.listPacks({ mine: true }),
        api.listGames(),
        api.listSessions(),
        bank.listQuestions(),
      ]);
      setAccount(me.account);
      setFormats(mine);
      setPacks(myPacks);
      setGameNames(new Map(games.map((g) => [g.id, g.name])));
      // The list also carries unowned seed shows; only the ones this account owns are its history.
      setHosted(sessions.filter((s) => s.ownerAccountId === me.account?.id));
      setQuestions(mineQuestions);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your account.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // A client-side route change doesn't scroll to its hash, so `/admin#templates` does it
  // here, once the profile above it has loaded and stopped moving it.
  const { hash } = useLocation();
  const loaded = account !== null;
  useEffect(() => {
    if (hash && loaded) document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [hash, loaded]);

  function onDelete(format: FormatSummary) {
    void deleteTemplate(format, setError).then((gone) => {
      if (gone) setFormats((current) => current.filter((f) => f.id !== format.id));
    });
  }

  return (
    <div className="admin">
      <div className="admin-header">
        <h1>Account</h1>
        <span className="spacer" />
        <Link className="btn btn-sm" to="/">
          Home
        </Link>
      </div>

      <div className="account-grid">
        <div className="col">
          {account ? (
            <ProfileCard
              account={account}
              onSaved={setAccount}
              onError={setError}
              onNotice={setNotice}
            />
          ) : null}
          <PasswordCard onError={setError} onNotice={setNotice} />

          <QuestionsPanel questions={questions} limit={ACCOUNT_SHOWN} />
        </div>

        <div className="col">
          <ResultsList limit={ACCOUNT_SHOWN} />
          <HostedList sessions={hosted} gameNames={gameNames} limit={ACCOUNT_SHOWN} />
          <TemplatesPanel formats={formats} onDelete={onDelete} limit={ACCOUNT_SHOWN} />
          <PacksPanel packs={packs} gameNames={gameNames} limit={ACCOUNT_SHOWN} />
        </div>
      </div>

      <Toast message={error} onDismiss={() => setError(null)} />
      <Toast message={notice} onDismiss={() => setNotice(null)} />
    </div>
  );
}

function ProfileCard({
  account,
  onSaved,
  onError,
  onNotice,
}: {
  account: AccountSummary;
  onSaved: (account: AccountSummary) => void;
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [displayName, setDisplayName] = useState(account.displayName);
  const [busy, setBusy] = useState(false);
  const dirty = displayName.trim() !== account.displayName && displayName.trim().length > 0;

  async function save(event: React.FormEvent) {
    event.preventDefault();
    if (!dirty) return;
    setBusy(true);
    try {
      const { account: updated } = await authApi.updateDisplayName(displayName.trim());
      onSaved(updated);
      // Keep the identity bar in step.
      noteSignedIn(updated);
      onNotice('Saved.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save your name.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel col" onSubmit={save}>
      <h2 className="panel-title">Profile</h2>
      <div className="field">
        <label htmlFor="displayName">Display name</label>
        <input
          id="displayName"
          value={displayName}
          maxLength={60}
          onChange={(e) => setDisplayName(e.target.value)}
        />
      </div>
      <div className="field">
        <label>Email</label>
        <span className="faint">{account.email ?? '—'}</span>
      </div>
      <div className="row">
        <button className="btn btn-primary" type="submit" disabled={!dirty || busy}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function PasswordCard({
  onError,
  onNotice,
}: {
  onError: (message: string) => void;
  onNotice: (message: string) => void;
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (next !== confirm) {
      onError('The new passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await authApi.changePassword(current, next);
      setCurrent('');
      setNext('');
      setConfirm('');
      onNotice('Password changed.');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="panel col" onSubmit={submit}>
      <h2 className="panel-title">Change password</h2>
      <div className="field">
        <label htmlFor="current-password">Current password</label>
        <input
          id="current-password"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </div>
      <div className="field">
        <label htmlFor="new-password">New password</label>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <span className="faint">At least {MIN_PASSWORD_LENGTH} characters.</span>
      </div>
      <div className="field">
        <label htmlFor="confirm-password">Confirm new password</label>
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </div>
      <div className="row">
        <button
          className="btn btn-primary"
          type="submit"
          disabled={busy || !current || !next || !confirm}
        >
          {busy ? 'Changing…' : 'Change password'}
        </button>
      </div>
    </form>
  );
}
